import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { EASE_OUT, SPRING } from 'shared/components/motion/tokens';
import { useRevealOnce } from 'shared/components/motion/useRevealOnce';
import { RevenuePoint } from 'shared/components/charts/types';
import { CHART_INK } from 'shared/constants/chartPalette';

interface RevenueTrendChartProps {
  points: RevenuePoint[];
  /** Mesmo eixo X do período anterior: vira uma linha tracejada de comparação. */
  comparePoints?: RevenuePoint[];
  compareLabel?: string;
  formatValue?: (value: number) => string;
  ariaLabel?: string;
}

const HEIGHT = 280;
const PADDING = { top: 40, right: 16, bottom: 36, left: 64 };
// Recuo horizontal dos pontos das pontas, para bolinha, rótulo e eixo X nunca serem cortados.
const INSET_X = 32;
const TICK_COUNT = 4;
// Com poucos pontos cada um ganha rótulo de valor; com muitos, os rótulos se atropelariam.
const MAX_LABELED_POINTS = 8;
const COMPARE_INK = '#94a3b8';

function formatCurrency(value: number) {
  return `R$ ${value.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
}

// Eixo Y enxuto: "R$ 20k" em vez de "R$ 20.000"; valores pequenos usam o próprio formatValue.
function formatTick(tick: number, formatValue: (value: number) => string) {
  if (Math.abs(tick) < 1000) return formatValue(tick);
  const compact = `${(tick / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}k`;
  return formatValue(tick).startsWith('R$') ? `R$ ${compact}` : compact;
}

// Passo "redondo" (1, 2, 2,5 ou 5 × 10ⁿ) para o eixo nunca mostrar 13.742.
function niceStep(raw: number) {
  const magnitude = 10 ** Math.floor(Math.log10(raw || 1));
  const step = [1, 2, 2.5, 5, 10].map((factor) => factor * magnitude).find((candidate) => candidate >= raw);
  return step ?? 10 * magnitude;
}

// Escala ajustada aos dados, não ao zero: numa série de tendência o que importa é a
// variação (15k → 19k). Começando do zero, ela vira uma linha quase reta encostada no topo.
// O eixo Y sempre mostra o valor real de cada grade, então a leitura continua honesta.
function niceDomain(values: number[]) {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const step = niceStep(((max - min) * 1.6 || Math.abs(max) || 1) / TICK_COUNT);
  let low = Math.floor(min / step) * step;
  if (min - low < step * 0.4) low -= step;
  low = Math.max(0, low);
  return { low, high: low + step * TICK_COUNT, step };
}

// Curva suave sem "passar do ponto": os controles só se deslocam em X (estilo monotoneX),
// então a linha nunca sugere um pico ou vale que não existe nos dados.
function smoothPath(coords: { x: number; y: number }[]) {
  return coords
    .map((point, index) => {
      if (index === 0) return `M ${point.x} ${point.y}`;
      const previous = coords[index - 1];
      const dx = (point.x - previous.x) / 3;
      return `C ${previous.x + dx} ${previous.y}, ${point.x - dx} ${point.y}, ${point.x} ${point.y}`;
    })
    .join(' ');
}

export function RevenueTrendChart({
  points,
  comparePoints,
  compareLabel = 'Período anterior',
  formatValue = formatCurrency,
  ariaLabel = 'Receita ao longo do tempo',
}: RevenueTrendChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [revealRef, inView] = useRevealOnce<SVGSVGElement>();
  const gradientId = `trend-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const [width, setWidth] = useState(640);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  // O SVG é desenhado na largura real do card (sem esticar), senão texto e bolinhas deformam.
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.max(280, entry.contentRect.width)));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const compare = comparePoints && comparePoints.length === points.length ? comparePoints : null;
  const { low, high, step } = niceDomain([...points, ...(compare ?? [])].map((point) => point.value));

  const plotLeft = PADDING.left + INSET_X;
  const plotRight = width - PADDING.right - INSET_X;
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;
  const baseline = PADDING.top + plotHeight;
  const stepX = points.length > 1 ? (plotRight - plotLeft) / (points.length - 1) : 0;
  const bandWidth = Math.min(stepX || 120, 120);
  const toX = (index: number) => (points.length > 1 ? plotLeft + index * stepX : (plotLeft + plotRight) / 2);
  const toY = (value: number) => PADDING.top + plotHeight - ((value - low) / (high - low)) * plotHeight;

  const coords = points.map((point, index) => ({ x: toX(index), y: toY(point.value) }));
  const compareCoords = compare ? compare.map((point, index) => ({ x: toX(index), y: toY(point.value) })) : [];

  const linePath = smoothPath(coords);
  const areaPath = coords.length
    ? `${linePath} L ${coords[coords.length - 1].x} ${baseline} L ${coords[0].x} ${baseline} Z`
    : '';
  const yTicks = Array.from({ length: TICK_COUNT + 1 }, (_, index) => low + index * step);

  // Muda quando os dados mudam (ex.: troca de período) e força a linha a se redesenhar.
  const dataKey = points.map((point) => `${point.label}:${point.value}`).join('|');
  const drawDuration = 1.2;
  const lastIndex = points.length - 1;
  const showValueLabels = points.length <= MAX_LABELED_POINTS;

  const hovered = hoverIndex !== null ? coords[hoverIndex] : null;
  const tooltipX = hovered ? Math.min(Math.max(hovered.x, 90), width - 90) : 0;
  const hoveredCompare = compare && hoverIndex !== null ? compare[hoverIndex].value : null;
  const hoveredDelta =
    hoveredCompare && hoverIndex !== null ? ((points[hoverIndex].value - hoveredCompare) / hoveredCompare) * 100 : null;

  return (
    <div>
      <div ref={containerRef} className="relative">
        <svg
          ref={revealRef}
          width={width}
          height={HEIGHT}
          role="img"
          aria-label={ariaLabel}
          className="block overflow-visible"
          onMouseLeave={() => setHoverIndex(null)}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={CHART_INK.brand} stopOpacity={0.32} />
              <stop offset="100%" stopColor={CHART_INK.brand} stopOpacity={0.02} />
            </linearGradient>
          </defs>

          {yTicks.map((tick, index) => {
            const y = toY(tick);
            return (
              <g key={tick}>
                <line
                  x1={PADDING.left}
                  y1={y}
                  x2={width - PADDING.right}
                  y2={y}
                  stroke={CHART_INK.grid}
                  strokeWidth={1}
                  strokeDasharray={index === 0 ? undefined : '4 4'}
                />
                <text x={PADDING.left - 12} y={y + 4} textAnchor="end" fontSize={11} fill={CHART_INK.muted}>
                  {formatTick(tick, formatValue)}
                </text>
              </g>
            );
          })}

          {/* Faixa da coluna em foco: deixa claro qual ponto o tooltip está descrevendo. */}
          {hovered && (
            <motion.rect
              initial={false}
              animate={{ x: hovered.x - bandWidth / 2 }}
              transition={SPRING}
              y={PADDING.top - 8}
              width={bandWidth}
              height={plotHeight + 8}
              rx={12}
              fill={CHART_INK.brand}
              fillOpacity={0.07}
            />
          )}

          <motion.path
            key={`area-${dataKey}`}
            d={areaPath}
            fill={`url(#${gradientId})`}
            initial={{ opacity: 0 }}
            animate={{ opacity: inView ? 1 : 0 }}
            transition={{ duration: 0.8, ease: EASE_OUT, delay: 0.4 }}
          />

          {compare && (
            <motion.path
              key={`compare-${dataKey}`}
              d={smoothPath(compareCoords)}
              fill="none"
              stroke={COMPARE_INK}
              strokeWidth={2}
              strokeDasharray="6 6"
              strokeLinecap="round"
              initial={{ opacity: 0 }}
              animate={{ opacity: inView ? 1 : 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            />
          )}

          <motion.path
            key={`line-${dataKey}`}
            d={linePath}
            fill="none"
            stroke={CHART_INK.brand}
            strokeWidth={3}
            strokeLinejoin="round"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: inView ? 1 : 0 }}
            transition={{ duration: drawDuration, ease: EASE_OUT }}
            style={{ filter: `drop-shadow(0 6px 8px ${CHART_INK.brand}55)` }}
          />

          {points.map((point, index) => {
            const { x, y } = coords[index];
            const isLast = index === lastIndex;
            const isActive = hoverIndex === index || (hoverIndex === null && isLast);
            const appearDelay = (index / Math.max(1, lastIndex)) * drawDuration * 0.8;

            return (
              <g key={point.label}>
                {compare && (
                  <motion.circle
                    cx={x}
                    cy={compareCoords[index].y}
                    r={3}
                    fill="#ffffff"
                    stroke={COMPARE_INK}
                    strokeWidth={1.5}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: inView ? 1 : 0 }}
                    transition={{ delay: 0.3 }}
                  />
                )}
                {isLast && inView && (
                  <motion.circle
                    cx={x}
                    cy={y}
                    fill={CHART_INK.brand}
                    initial={{ r: 6, opacity: 0.35 }}
                    animate={{ r: 16, opacity: 0 }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut', delay: drawDuration }}
                  />
                )}
                <motion.circle
                  key={`dot-${dataKey}-${index}`}
                  cx={x}
                  cy={y}
                  fill={isActive ? CHART_INK.brand : '#ffffff'}
                  stroke={CHART_INK.brand}
                  strokeWidth={2.5}
                  initial={{ r: 0 }}
                  animate={{ r: inView ? (isActive ? 6.5 : 4.5) : 0 }}
                  transition={
                    inView
                      ? { type: 'spring', stiffness: 500, damping: 20, delay: hoverIndex === null ? appearDelay : 0 }
                      : undefined
                  }
                />
                {showValueLabels && (
                  <motion.text
                    key={`label-${dataKey}-${index}`}
                    x={x}
                    textAnchor="middle"
                    fontSize={isActive ? 13 : 12}
                    fontWeight={isActive ? 700 : 600}
                    fill={isActive ? CHART_INK.primary : CHART_INK.secondary}
                    initial={{ opacity: 0, y: y - 8 }}
                    animate={inView ? { opacity: hoverIndex === index ? 0 : 1, y: y - 16 } : {}}
                    transition={{ duration: 0.4, ease: EASE_OUT, delay: hoverIndex === null ? appearDelay + 0.15 : 0 }}
                  >
                    {formatValue(point.value)}
                  </motion.text>
                )}
                <text
                  x={x}
                  y={HEIGHT - 10}
                  textAnchor="middle"
                  fontSize={12}
                  fontWeight={isActive ? 600 : 400}
                  fill={isActive ? CHART_INK.primary : CHART_INK.muted}
                >
                  {point.label}
                </text>
                <rect
                  x={x - (stepX || plotRight - plotLeft) / 2}
                  y={PADDING.top}
                  width={stepX || plotRight - plotLeft}
                  height={plotHeight + PADDING.bottom}
                  fill="transparent"
                  onMouseEnter={() => setHoverIndex(index)}
                  onFocus={() => setHoverIndex(index)}
                  onBlur={() => setHoverIndex(null)}
                  tabIndex={0}
                  role="button"
                  aria-label={`${point.label}: ${formatValue(point.value)}`}
                  className="cursor-pointer outline-none"
                />
              </g>
            );
          })}
        </svg>

        {hovered && hoverIndex !== null && (
          // Fora: posição (desliza com mola entre os pontos). Dentro: centraliza acima do ponto.
          <motion.div
            className="pointer-events-none absolute left-0 top-0 z-10"
            initial={{ x: tooltipX, y: hovered.y - 14 }}
            animate={{ x: tooltipX, y: hovered.y - 14 }}
            transition={SPRING}
          >
            <motion.div
              key={hoverIndex}
              className="min-w-[168px] rounded-xl border border-border/60 bg-surface/95 px-3.5 py-2.5 text-xs shadow-card-hover backdrop-blur"
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2, ease: EASE_OUT }}
              style={{ x: '-50%', y: '-100%', transformOrigin: 'bottom center' }}
            >
              <p className="font-medium text-ink-secondary">{points[hoverIndex].label}</p>
              <p className="mt-0.5 flex items-center gap-2 text-sm font-bold text-ink">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: CHART_INK.brand }} />
                {formatValue(points[hoverIndex].value)}
              </p>
              {hoveredCompare !== null && hoveredDelta !== null && (
                <>
                  <p className="mt-0.5 flex items-center gap-2 text-ink-secondary">
                    <span className="h-2 w-2 rounded-full border-2" style={{ borderColor: COMPARE_INK }} />
                    {formatValue(hoveredCompare)} no {compareLabel.toLowerCase()}
                  </p>
                  <p
                    className={`mt-1.5 flex items-center gap-1 font-semibold ${
                      hoveredDelta >= 0 ? 'text-success' : 'text-danger'
                    }`}
                  >
                    {hoveredDelta >= 0 ? (
                      <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.5} aria-label="alta de" />
                    ) : (
                      <ArrowDownRight className="h-3.5 w-3.5" strokeWidth={2.5} aria-label="queda de" />
                    )}
                    {Math.abs(hoveredDelta).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% de variação
                  </p>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </div>

      {compare && (
        <div className="mt-3 flex flex-wrap gap-5 text-sm">
          <span className="flex items-center gap-2 text-ink-secondary">
            <span className="h-[3px] w-5 rounded-full" style={{ backgroundColor: CHART_INK.brand }} />
            Este período
          </span>
          <span className="flex items-center gap-2 text-ink-secondary">
            <span className="w-5 border-t-2 border-dashed" style={{ borderColor: COMPARE_INK }} />
            {compareLabel}
          </span>
        </div>
      )}
    </div>
  );
}
